import { MailOutlined } from '@ant-design/icons';
import { getCountryName } from '@mahawi/eshop-common/src/get-country-name';
import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  COMPLETED_ORDER_STATUS_CODES,
  type EOrderStatus,
} from '@mahawi/eshop-common/src/types';
import { Button, Popconfirm, Select, Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { type Key } from 'antd/es/table/interface';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENotificationSeverity } from '../../admin-types';
import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { getTagByRelativeDate } from '../fragments/get-tag-by-relative-date';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { notificationContext } from '../notification';
import { type RootState } from '../reducers';
import {
  marketingConsentCampaignEligibleList,
  marketingConsentCampaignSend,
} from '../reducers/marketing-consent-campaign/reducer';
import { type IMarketingConsentCampaignEligibleCustomer } from '../reducers/marketing-consent-campaign/types';
import { SPACING } from '../theme/tokens';

interface DataType extends IMarketingConsentCampaignEligibleCustomer {
  key: string;
}

function MarketingConsentCampaignEligibleContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const eligibleCustomers = useSelector(
    (s: RootState) => s.MarketingConsentCampaign.eligibleCustomers,
  );
  const totalCount = useSelector(
    (s: RootState) => s.MarketingConsentCampaign.totalCount,
  );
  const inProcess = useSelector(
    (s: RootState) => s.MarketingConsentCampaign.inProcess,
  );
  const sent = useSelector((s: RootState) => s.MarketingConsentCampaign.sent);
  const skipped = useSelector(
    (s: RootState) => s.MarketingConsentCampaign.skipped,
  );

  const { openNotification } = useContext(notificationContext);

  const [columns, setColumns] = useState<ColumnsType>([]);
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [filteredDataSource, setFilteredDataSource] = useState<DataType[]>([]);
  const [selectedUUIDs, setSelectedUUIDs] = useState<string[]>([]);
  const [orderStatusCodes, setOrderStatusCodes] = useState<string[]>([]);

  const isFirstRender = useRef(true);

  useEffect((): void => {
    dispatch(marketingConsentCampaignEligibleList({}));
  }, [dispatch]);

  useEffect((): void => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!sent || !skipped) {
      return;
    }

    openNotification('topRight', {
      severity: ENotificationSeverity.SUCCESS,
      title: 'Marketing consent campaign sent',
      description: `Sent to ${sent.length}, skipped ${skipped.length}.`,
    });
  }, [sent, skipped, openNotification]);

  useEffect((): void => {
    const dataSourceUE: DataType[] | undefined = eligibleCustomers?.map(
      (c: IMarketingConsentCampaignEligibleCustomer): DataType => ({
        ...c,
        key: c.uuid,
      }),
    );

    const columnsUE: ColumnsType = [
      {
        title: 'Email',
        dataIndex: 'email',
        render: (
          email: string | undefined,
          record: DataType,
        ): React.ReactElement => (
          <Link
            to={`/crm/customer/${record.uuid}`}
            label={email ?? record.uuid}
            openInNewTab
          />
        ),
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.email ?? '', b.email ?? ''),
      },
      {
        title: 'First Name',
        dataIndex: 'firstName',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.firstName ?? '', b.firstName ?? ''),
      },
      {
        title: 'Last Name',
        dataIndex: 'lastName',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.lastName ?? '', b.lastName ?? ''),
      },
      {
        title: 'Country',
        dataIndex: 'countryTypeIsoCode',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.countryTypeIsoCode ?? '', b.countryTypeIsoCode ?? ''),
        filters: [
          ...new Set(
            dataSourceUE
              ?.map(
                ({ countryTypeIsoCode }: DataType): string | undefined =>
                  countryTypeIsoCode,
              )
              .filter((c): c is string => !!c),
          ),
        ]
          .sort()
          .map((c: string) => ({
            text: `${getFlag({ countryTypeIsoCode: c })} ${getCountryName({ countryTypeIsoCode: c })} (${c})`,
            value: c,
          })),
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.countryTypeIsoCode === value,
        render: (c: string | undefined): string =>
          c
            ? `${getFlag({ countryTypeIsoCode: c })} ${getCountryName({ countryTypeIsoCode: c })} (${c})`
            : EMPTY_VALUE,
      },
      {
        title: 'Last Completed Order',
        dataIndex: 'lastCompletedOrderAt',
        sorter: (a: DataType, b: DataType): number =>
          new Date(a.lastCompletedOrderAt).getTime() -
          new Date(b.lastCompletedOrderAt).getTime(),
        defaultSortOrder: 'descend',
        render: (lastCompletedOrderAt: string): React.ReactElement =>
          getTagByRelativeDate({ date: new Date(lastCompletedOrderAt) }),
      },
    ];

    setColumns(columnsUE);

    if (!dataSourceUE) {
      return;
    }

    setDataSource(dataSourceUE);
    setFilteredDataSource(dataSourceUE);
  }, [eligibleCustomers]);

  return (
    <Space orientation="vertical" size={SPACING.lg} style={{ width: '100%' }}>
      <DetailPageHeader
        title="Marketing consent campaign"
        onReload={(): void => {
          dispatch(marketingConsentCampaignEligibleList({ orderStatusCodes }));
        }}
        isReloadDisabled={inProcess}
        actions={
          <>
            <Select
              mode="multiple"
              placeholder="Order status"
              style={{ minWidth: 240 }}
              allowClear
              value={orderStatusCodes}
              onChange={(value: string[]): void => setOrderStatusCodes(value)}
              options={COMPLETED_ORDER_STATUS_CODES.map(
                (code: EOrderStatus) => ({
                  label: code,
                  value: code,
                }),
              )}
              disabled={inProcess}
            />

            {filteredDataSource.length > 0 && (
              <Button
                onClick={(): void => {
                  setSelectedUUIDs(filteredDataSource.map((c) => c.uuid));
                }}
                disabled={inProcess}
              >
                {`Select all eligible (${filteredDataSource.length})`}
              </Button>
            )}

            {selectedUUIDs.length > 0 && (
              <Button
                onClick={(): void => setSelectedUUIDs([])}
                disabled={inProcess}
              >
                Clear selection
              </Button>
            )}

            <Popconfirm
              placement="bottom"
              title={`Send marketing consent campaign to ${selectedUUIDs.length} customer${selectedUUIDs.length === 1 ? '' : 's'}?`}
              description="Emails ask customers to explicitly confirm marketing consent. Eligibility is re-checked at send time, so some may be skipped if they no longer qualify."
              onConfirm={(): void => {
                dispatch(marketingConsentCampaignSend(selectedUUIDs));
                setSelectedUUIDs([]);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                icon={<MailOutlined />}
                disabled={selectedUUIDs.length === 0 || inProcess}
                loading={inProcess}
              >
                {`Send now (${selectedUUIDs.length})`}
              </Button>
            </Popconfirm>
          </>
        }
        info={
          totalCount !== undefined && (
            <Typography.Text type="secondary">
              {`${totalCount} eligible customer${totalCount === 1 ? '' : 's'}`}
            </Typography.Text>
          )
        }
      />

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={inProcess && !eligibleCustomers}
        scroll={{ x: true }}
        onChange={(_pagination, _filters, _sorter, extra): void => {
          setFilteredDataSource(extra.currentDataSource as DataType[]);
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedUUIDs,
          onChange: (keys): void => {
            setSelectedUUIDs(keys as string[]);
          },
        }}
      />
    </Space>
  );
}

export default MarketingConsentCampaignEligibleContainer;
